import { useState } from 'react';

const ReservationInfoModal = ({
  setShowReservationInfoModal,
  setSelectedReservation,
  reservation,
  renterName,
  equipmentName,
}) => {
  const [selectedImage, setSelectedImage] = useState(null);

  const options = {
    day: 'numeric',
    year: 'numeric',
    month: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  };

  if (!reservation || typeof reservation === 'undefined') {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-md p-6">
          <h2 className="text-2xl font-semibold mb-4 text-gray-800">
            There was an error getting this reservations information.
          </h2>
          <div className="mt-6 flex justify-end">
            <button
              className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
              onClick={() => {
                setShowReservationInfoModal(false);
                setSelectedReservation(null);
              }}
              type="button"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  }

  // For displaying large images when clicked
  const ImageModal = ({ imageSrc, onClose }) => (
    <div
      className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-75 z-[60]"
      onClick={onClose}
    >
      <div className="relative max-w-4xl max-h-[90vh]">
        <button
          className="absolute top-3 right-3 text-white bg-red-600 rounded-full w-8 h-8 flex items-center justify-center"
          onClick={onClose}
        >
          ×
        </button>
        <img
          src={imageSrc}
          alt="Damage full view"
          className="max-w-full max-h-[85vh] object-contain"
        />
      </div>
    </div>
  );

  const isReturned = reservation.reservationStatus === 'returned';
  const hasDamage = isReturned && reservation.damageReport && reservation.damageReport.hasDamage;

  return (
    <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
      <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-md p-6 max-h-[90vh] overflow-y-auto">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Reservation Information</h2>

        {/* Basic reservation information */}
        <div className="space-y-2">
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Renter Name:</span>
            <span className="text-gray-900">{renterName}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Equipment:</span>
            <span className="text-gray-900">{equipmentName}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Start Date:</span>
            <span className="text-gray-900">
              {new Date(reservation.startDate).toLocaleString('en-US', options)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">End Date:</span>
            <span className="text-gray-900">
              {new Date(reservation.endDate).toLocaleString('en-US', options)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Quantity:</span>
            <span className="text-gray-900">{reservation.rentalTotal}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Delivery Assistance:</span>
            <span className="text-gray-900">{reservation.deliveryAssistance ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Pickup Assistance:</span>
            <span className="text-gray-900">{reservation.pickupAssistance ? 'Yes' : 'No'}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Price:</span>
            <span className="text-gray-900">${reservation.rentalPrice}</span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Status:</span>
            <span
              className={`px-2 py-1 rounded-full text-sm font-medium ${
                reservation.reservationStatus === 'pending'
                  ? 'bg-yellow-100 text-yellow-800'
                  : reservation.reservationStatus === 'approved'
                    ? 'bg-green-100 text-green-800'
                    : reservation.reservationStatus === 'in-use'
                      ? 'bg-blue-100 text-blue-800'
                      : reservation.reservationStatus === 'canceled'
                        ? 'bg-red-100 text-red-800'
                        : reservation.reservationStatus === 'filled'
                          ? 'bg-purple-100 text-purple-800'
                          : reservation.reservationStatus === 'returned'
                            ? 'bg-gray-100 text-gray-800'
                            : 'bg-gray-100 text-gray-800'
              }`}
            >
              {reservation.reservationStatus
                ? reservation.reservationStatus.charAt(0).toUpperCase() +
                  reservation.reservationStatus.slice(1)
                : 'N/A'}
            </span>
          </div>
          {reservation.rentalDuration && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Duration:</span>
              <span className="text-gray-900">{reservation.rentalDuration}</span>
            </div>
          )}
          {reservation.paymentMethod && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Payment Method:</span>
              <span className="text-gray-900">
                {reservation.paymentMethod
                  .replace('_', ' ')
                  .replace(/\b\w/g, (l) => l.toUpperCase())}
              </span>
            </div>
          )}
          {reservation.pickupLocation && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Pickup Location:</span>
              <span className="text-gray-900">{reservation.pickupLocation}</span>
            </div>
          )}
          {reservation.dropOffLocation && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Drop-off Location:</span>
              <span className="text-gray-900">{reservation.dropOffLocation}</span>
            </div>
          )}
          {reservation.additionalNotes && (
            <div className="flex justify-between">
              <span className="font-medium text-gray-700">Additional Notes:</span>
              <span className="text-gray-900">{reservation.additionalNotes}</span>
            </div>
          )}
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Created:</span>
            <span className="text-gray-900">
              {new Date(reservation.createdAt).toLocaleString('en-US', options)}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="font-medium text-gray-700">Last Updated:</span>
            <span className="text-gray-900">
              {new Date(reservation.updatedAt).toLocaleString('en-US', options)}
            </span>
          </div>

          {/* Return information section*/}
          {isReturned && (
            <>
              <div className="mt-6 mb-4 pt-4 border-t border-gray-200">
                <h3 className="text-xl font-semibold text-gray-800">Return Details</h3>
              </div>

              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Return Date:</span>
                <span className="text-gray-900">
                  {reservation.returnDate
                    ? new Date(reservation.returnDate).toLocaleString('en-US', options)
                    : 'N/A'}
                </span>
              </div>

              <div className="flex justify-between">
                <span className="font-medium text-gray-700">Return Condition:</span>
                <span
                  className={`px-2 rounded-full text-white ${
                    reservation.returnCondition === 'excellent'
                      ? 'bg-green-500'
                      : reservation.returnCondition === 'good'
                        ? 'bg-blue-500'
                        : reservation.returnCondition === 'fair'
                          ? 'bg-yellow-500'
                          : reservation.returnCondition === 'poor'
                            ? 'bg-orange-500'
                            : reservation.returnCondition === 'damaged'
                              ? 'bg-red-500'
                              : 'bg-gray-500'
                  }`}
                >
                  {reservation.returnCondition
                    ? reservation.returnCondition.charAt(0).toUpperCase() +
                      reservation.returnCondition.slice(1)
                    : 'N/A'}
                </span>
              </div>

              {hasDamage && (
                <>
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <h4 className="text-lg font-medium text-red-600 mb-2">Damage Report</h4>

                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Description:</span>
                      <div className="mt-1 p-2 bg-gray-50 rounded-md text-gray-800">
                        {reservation.damageReport.description || 'No description provided'}
                      </div>
                    </div>

                    <div className="mb-2">
                      <span className="font-medium text-gray-700">Estimated Repair Cost:</span>
                      <div className="mt-1 text-gray-800">
                        ${reservation.damageReport.estimatedRepairCost || '0'}
                      </div>
                    </div>

                    {reservation.damageReport.images &&
                      reservation.damageReport.images.length > 0 && (
                        <div className="mb-2">
                          <span className="font-medium text-gray-700">Damage Photos:</span>
                          <div className="mt-2 grid grid-cols-3 gap-2">
                            {reservation.damageReport.images.map((image, index) => (
                              <div
                                key={index}
                                className="relative cursor-pointer"
                                onClick={() => setSelectedImage(image)}
                              >
                                <img
                                  src={image}
                                  alt={`Damage ${index + 1}`}
                                  className="h-20 w-full object-cover rounded"
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                  </div>
                </>
              )}

              {reservation.returnNotes && (
                <div className="mt-3 mb-2">
                  <span className="font-medium text-gray-700">Return Notes:</span>
                  <div className="mt-1 p-2 bg-gray-50 rounded-md text-gray-800">
                    {reservation.returnNotes}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="mt-6 flex justify-end">
          <button
            className="bg-red-500 hover:bg-red-600 text-white font-semibold py-2 px-5 rounded-md transition duration-200"
            onClick={() => {
              setShowReservationInfoModal(false);
              setSelectedReservation(null);
            }}
            type="button"
          >
            Close
          </button>
        </div>

        {selectedImage && (
          <ImageModal imageSrc={selectedImage} onClose={() => setSelectedImage(null)} />
        )}
      </div>
    </div>
  );
};

export { ReservationInfoModal };
